"use client";

import { availabilityLabel, isAvailabilityFresh } from "@/lib/availability";
import Link from "next/link";
import Image from "next/image";
import { motion } from "motion/react";
import { Star, Clock } from "lucide-react";
import { getTradeName } from "@/lib/trades";
import { SaveButton } from "@/app/components/SaveButton";
import { TrustBadges } from "@/app/components/TrustBadges";
import { useQuoteShortlist } from "./QuoteShortlist";

export interface TradesPerson {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  business_name: string | null;
  photo_url: string | null;
  trade_categories: string[];
  postcode: string | null;
  service_radius_miles: number;
  availability_status: string;
  availability_confirmed_at?: string | null;
  years_experience: number | null;
  bio: string | null;
  distance_miles: number | null;
  avg_rating: number;
  review_count: number;
  work_gallery?: string[];
  qualifications?: unknown[];
  referral_tier?: string | null;
  last_seen_at?: string | null;
  avg_response_time_hours?: number | null;
  has_public_liability_insurance?: boolean;
  lat?: number | null;
  lng?: number | null;
  vouch_count?: number;
  showcase_count?: number;
  next_available_date?: string | null;
  verified_at?: string | null;
  pli_coverage?: string | null;
  response_time_count?: number;
  external_reviews?: unknown[];
  has_availability_set?: boolean;
  google_rating?: number;
  google_review_count?: number;
}

const TIER_LABELS: Record<string, string> = {
  founding: "Founding",
  early_supporter: "Early Supporter",
  priority: "Priority",
};

function getQualificationNames(qualifications: unknown[] | undefined): string[] {
  const names: string[] = [];
  if (!Array.isArray(qualifications)) return names;
  for (const q of qualifications) {
    if (
      q &&
      typeof q === "object" &&
      "name" in (q as Record<string, unknown>)
    ) {
      const name = (q as Record<string, unknown>).name;
      if (typeof name === "string") names.push(name);
    }
  }
  return names;
}

const AVAILABILITY: Record<
  string,
  { label: string; dotClass: string; textClass: string; pulse: boolean }
> = {
  available: {
    label: "Available for enquiries",
    dotClass: "bg-emerald-500",
    textClass: "text-emerald-700",
    pulse: true,
  },
  busy: {
    label: "Busy",
    dotClass: "bg-amber-500",
    textClass: "text-amber-700",
    pulse: false,
  },
  not_taking_work: {
    label: "Not taking work",
    dotClass: "bg-text-muted",
    textClass: "text-text-muted",
    pulse: false,
  },
};

export function TradespersonCard({
  person,
  isSaved = false,
  isLoggedIn = false,
  isHomeowner = false,
  profileComplete = false,
}: {
  person: TradesPerson;
  isSaved?: boolean;
  isLoggedIn?: boolean;
  isHomeowner?: boolean;
  profileComplete?: boolean;
}) {
  const initials = `${person.first_name[0]}${person.last_name[0]}`.toUpperCase();
  const qualificationNames = getQualificationNames(person.qualifications);

  const tierLabel =
    person.referral_tier && person.referral_tier !== "none"
      ? TIER_LABELS[person.referral_tier] ?? null
      : null;

  const avail = (isAvailabilityFresh(person.availability_confirmed_at) ? AVAILABILITY[person.availability_status] : null) ?? {
    label: availabilityLabel(person.availability_status, person.availability_confirmed_at),
    dotClass: "bg-text-muted",
    textClass: "text-text-muted",
    pulse: false,
  };

  const { toggle, has, isFull } = useQuoteShortlist();
  const isInShortlist = has(person.user_id);

  const distanceText =
    person.distance_miles != null
      ? person.distance_miles < 1
        ? "Less than a mile away"
        : `${person.distance_miles.toFixed(1)} miles away`
      : null;

  const tradeDisplay = person.trade_categories
    .slice(0, 2)
    .map((t) => getTradeName(t))
    .join(", ");
  const extraTrades =
    person.trade_categories.length > 2
      ? ` +${person.trade_categories.length - 2}`
      : "";

  return (
    <motion.article
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      className="bg-white rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row gap-8 hover:bg-cream/50 transition-all duration-500 group border border-stone/10 shadow-sm hover:shadow-md"
    >
      {/* Photo */}
      <div className="relative shrink-0">
        <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl overflow-hidden shadow-inner bg-cream">
          {person.photo_url ? (
            <Image
              src={person.photo_url}
              alt={`${person.first_name} ${person.last_name}`}
              width={160}
              height={160}
              className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-700 ease-out"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <span className="text-3xl font-bold text-forest">{initials}</span>
            </div>
          )}
        </div>
        {tierLabel && (
          <div className="absolute -bottom-2 -right-2 bg-copper text-white px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase shadow-sm border border-copper/10">
            {tierLabel}
          </div>
        )}
        <div className="absolute top-1 right-1">
          <SaveButton
            tradespersonId={person.user_id}
            initialSaved={isSaved}
            isLoggedIn={isLoggedIn}
            isHomeowner={isHomeowner}
            size="sm"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-grow">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-4">
          <div>
            <Link
              href={`/tradesperson/${person.user_id}`}
              className="no-underline"
            >
              <h2 className="text-2xl font-serif font-extrabold text-charcoal group-hover:text-forest transition-colors">
                {person.first_name} {person.last_name}
              </h2>
            </Link>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sage font-bold text-sm">
                {tradeDisplay}
                {extraTrades}
              </p>
              <span
                className={`w-1.5 h-1.5 rounded-full ${avail.dotClass} ${avail.pulse ? "animate-pulse" : ""}`}
              />
              <span
                className={`text-[10px] font-bold uppercase tracking-wider ${avail.textClass}`}
              >
                {avail.label}
              </span>
            </div>
            {person.next_available_date && person.next_available_date !== "none" && (
              <p className="text-xs text-warning font-medium mt-0.5">
                Calendar gap: {new Date(person.next_available_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
              </p>
            )}
          </div>

          <div className="sm:text-right">
            {person.avg_rating > 0 ? (
              <>
                <div className="flex items-center sm:justify-end gap-1.5 text-forest font-bold">
                  <Star className="w-5 h-5 fill-forest" />
                  <span className="text-lg">{person.avg_rating.toFixed(1)}</span>
                  <span className="text-text-muted font-normal text-xs">
                    ({person.review_count}{" "}
                    {person.review_count === 1 ? "review" : "reviews"})
                  </span>
                </div>
                {(person.google_review_count ?? 0) > 0 && (
                  <p className="text-xs text-text-muted mt-1 font-medium">
                    Google {person.google_rating?.toFixed(1)} ({person.google_review_count})
                  </p>
                )}
              </>
            ) : (person.google_review_count ?? 0) > 0 ? (
              <div className="flex items-center sm:justify-end gap-1.5 text-forest font-bold">
                <Star className="w-5 h-5 fill-forest" />
                <span className="text-lg">Google {person.google_rating?.toFixed(1)}</span>
                <span className="text-text-muted font-normal text-xs">
                  ({person.google_review_count})
                </span>
              </div>
            ) : (
              <span className="text-xs text-text-muted italic">
                No reviews yet
              </span>
            )}
            {(person.vouch_count ?? 0) > 0 && (
              <p className="text-xs text-text-muted mt-1 font-medium">
                {person.vouch_count} vouch{person.vouch_count !== 1 ? "es" : ""}
              </p>
            )}
            {distanceText && (
              <p className="text-text-muted text-xs mt-1 font-medium">
                {distanceText}
              </p>
            )}
          </div>
        </div>

        {/* Trust badges */}
        <div className="mb-2 flex items-center gap-2 flex-wrap">
          <TrustBadges
            avgResponseTimeHours={person.avg_response_time_hours}
            responseTimeCount={person.response_time_count ?? 0}
            hasPublicLiabilityInsurance={person.has_public_liability_insurance}
            pliCoverage={person.pli_coverage}
            verifiedAt={person.verified_at}
            layout="compact"
          />
          {profileComplete && (
            <span className="inline-flex items-center gap-1 bg-forest/10 text-forest px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-tight border border-forest/20">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Complete profile
            </span>
          )}
        </div>

        {/* Qualification badges */}
        <div className="flex flex-wrap gap-2 mb-4">
          {qualificationNames.slice(0, 4).map((name) => (
            <span
              key={name}
              className="bg-sage-pale text-forest px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tight border border-sage/10"
            >
              {name}
            </span>
          ))}
          {(person.showcase_count ?? 0) > 0 && (
            <span className="bg-sage-pale text-forest px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-tight border border-sage/10">
              {person.showcase_count} showcase{person.showcase_count !== 1 ? "s" : ""}
            </span>
          )}
        </div>

        {/* Bio */}
        {person.bio && (
          <p className="text-text-muted text-sm leading-relaxed mb-6 line-clamp-2 font-medium">
            {person.bio}
          </p>
        )}

        {/* Bottom row */}
        <div className="flex items-center justify-between border-t border-stone/20 pt-6">
          <div className="flex gap-8">
            {person.years_experience != null && person.years_experience > 0 && (
              <div className="flex flex-col">
                <span className="text-[10px] uppercase font-bold text-text-muted tracking-wider mb-1">
                  Experience
                </span>
                <span className="text-sm font-bold text-charcoal flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-sage" />
                  {person.years_experience}{" "}
                  {person.years_experience === 1 ? "year" : "years"}
                </span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {isHomeowner && (
              <button
                onClick={() =>
                  toggle({
                    userId: person.user_id,
                    name: `${person.first_name} ${person.last_name}`,
                    photoUrl: person.photo_url,
                  })
                }
                disabled={!isInShortlist && isFull}
                className={`px-4 py-3 rounded-sm font-serif font-bold text-sm transition-all shadow-sm ${
                  isInShortlist
                    ? "bg-forest text-white"
                    : "border border-forest text-forest hover:bg-forest hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-forest"
                }`}
              >
                {isInShortlist ? "✓ Shortlisted" : "Request Quote"}
              </button>
            )}
            <Link
              href={`/tradesperson/${person.user_id}`}
              className="bg-sage-pale text-forest px-6 py-3 rounded-sm font-serif font-bold text-sm hover:bg-forest hover:text-white transition-all shadow-sm no-underline"
            >
              View Profile
            </Link>
          </div>
        </div>
      </div>
    </motion.article>
  );
}
